import type { Tag, UserTag, UserTagWithDetails } from "@/domain/tags";
import { apiClientJSON } from "../apiClient";

const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };
type JsonObject = { [key: string]: JsonValue };

/** Raw API response structure for user tag with associated tag (snake_case from API) */
interface RawUserTagWithTag {
  user_tag: JsonObject;
  tag: JsonObject;
}

function transformKeys<R>(obj: JsonValue): R {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys<JsonValue>(item)) as R;
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = toCamelCase(key);
      acc[camelKey] = transformKeys<JsonValue>((obj as JsonObject)[key]);
      return acc;
    }, {} as JsonObject) as R;
  }
  return obj as unknown as R;
}

export const getTags = async (isArchived: boolean = false): Promise<Tag[]> => {
  return apiClientJSON
    .get("/backend/api/v1/admin/tags", {
      params: { is_archived: isArchived },
    })
    .then((r) => transformKeys(r.data.body.tags));
};

export const createTag = async (
  name: string,
  color: string,
  createdByEmail: string,
  description?: string
): Promise<Tag> => {
  return apiClientJSON
    .post("/backend/api/v1/admin/tags", {
      name,
      color,
      created_by_email: createdByEmail,
      description,
    })
    .then((r) => transformKeys(r.data.body.tag));
};

export const getUserTags = async (
  userId: string
): Promise<UserTagWithDetails[]> => {
  const [userTagsResponse, tagsResponse] = await Promise.all([
    apiClientJSON.get("/backend/api/v1/admin/user-tags", {
      params: { user_id: userId, is_archived: false },
    }),
    apiClientJSON.get("/backend/api/v1/admin/tags", {
      params: { is_archived: false },
    }),
  ]);

  const userTags: UserTag[] = transformKeys(
    userTagsResponse.data.body.user_tags
  );
  const tags: Tag[] = transformKeys(tagsResponse.data.body.tags);

  const tagMap = new Map(tags.map((t) => [t.id, t]));

  return userTags
    .filter((ut) => tagMap.has(ut.tagId))
    .map((ut) => {
      const tag = tagMap.get(ut.tagId);
      // Tag is guaranteed to exist due to the filter above
      return { ...ut, tag: tag as Tag };
    });
};

export const addTagToUser = async (
  userId: string,
  tagId: string,
  appliedByEmail: string,
  note?: string
): Promise<UserTag> => {
  return apiClientJSON
    .post("/backend/api/v1/admin/user-tags", {
      user_id: userId,
      tag_id: tagId,
      applied_by_email: appliedByEmail,
      note,
    })
    .then((r) => transformKeys(r.data.body.user_tag));
};

export const removeTagFromUser = async (
  userId: string,
  tagId: string
): Promise<boolean> => {
  return apiClientJSON
    .delete("/backend/api/v1/admin/user-tags", {
      data: { user_id: userId, tag_id: tagId },
    })
    .then((r) => r.data.body.success);
};

export const archiveTag = async (
  tagId: string,
  isArchived: boolean = true
): Promise<Tag> => {
  return apiClientJSON
    .post("/backend/api/v1/admin/tags/archive", {
      tag_id: tagId,
      is_archived: isArchived,
    })
    .then((r) => transformKeys(r.data.body.tag));
};

export const getUserTagsBatch = async (
  userIds: string[]
): Promise<Map<string, UserTagWithDetails[]>> => {
  if (userIds.length === 0) {
    return new Map();
  }

  const [userTagsResponse, tagsResponse] = await Promise.all([
    apiClientJSON.get("/backend/api/v1/admin/user-tags", {
      params: { user_ids: userIds.join(","), is_archived: false },
    }),
    apiClientJSON.get("/backend/api/v1/admin/tags", {
      params: { is_archived: false },
    }),
  ]);

  const userTags: UserTag[] = transformKeys(
    userTagsResponse.data.body.user_tags
  );
  const tags: Tag[] = transformKeys(tagsResponse.data.body.tags);

  const tagMap = new Map(tags.map((t) => [t.id, t]));
  const result = new Map<string, UserTagWithDetails[]>();

  for (const ut of userTags) {
    const tag = tagMap.get(ut.tagId);
    if (tag) {
      const userTagWithDetails: UserTagWithDetails = {
        ...ut,
        tag,
      };
      const existing = result.get(ut.userId);
      if (existing) {
        existing.push(userTagWithDetails);
      } else {
        result.set(ut.userId, [userTagWithDetails]);
      }
    }
  }

  return result;
};

export const getUserIdsByTag = async (tagId: string): Promise<string[]> => {
  const response = await apiClientJSON.get("/backend/api/v1/admin/user-tags", {
    params: { tag_id: tagId },
  });
  const userTags: UserTag[] = transformKeys(response.data.body.user_tags);
  return [...new Set(userTags.map((ut) => ut.userId))];
};

export interface LegacyUserIdsByTagResult {
  legacyUserIds: string[];
  totalCount: number;
}

export const getLegacyUserIdsByTag = async (
  tagIds: string[],
  match: "AND" | "OR" = "OR",
  page?: number,
  size?: number
): Promise<LegacyUserIdsByTagResult> => {
  const params: Record<string, string | number> = {
    tag_ids: tagIds.join(","),
    match,
  };
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;

  const response = await apiClientJSON.get(
    "/backend/api/v1/admin/user-tags/legacy-user-ids-by-tag",
    {
      params,
    }
  );
  return {
    legacyUserIds: response.data.body.legacy_user_ids,
    totalCount: response.data.body.total_count,
  };
};

export const getUserTagsByLegacyIds = async (
  legacyIds: string[]
): Promise<Map<string, UserTagWithDetails[]>> => {
  if (legacyIds.length === 0) {
    return new Map();
  }

  const response = await apiClientJSON.post(
    "/backend/api/v1/admin/user-tags/by-legacy-ids",
    {
      legacy_ids: legacyIds,
    }
  );

  const tagsByLegacyId = response.data.body.tags_by_legacy_id;
  const result = new Map<string, UserTagWithDetails[]>();

  for (const [legacyId, tags] of Object.entries(tagsByLegacyId)) {
    const transformedTags = (tags as RawUserTagWithTag[]).map(
      (t): UserTagWithDetails => {
        const userTag = transformKeys<UserTag>(t.user_tag as JsonValue);
        const tag = transformKeys<Tag>(t.tag as JsonValue);
        return { ...userTag, tag };
      }
    );
    result.set(legacyId, transformedTags);
  }

  return result;
};
